import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { magicLinkService } from '@/services/supabase/auth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const VerifyMagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      const redirectTo = searchParams.get('redirect_to') || '/dashboard';

      if (!token) {
        setStatus('error');
        setErrorMessage('No token provided');
        return;
      }

      try {
        setStatus('loading');
        console.log('[VerifyMagicLink] Starting verification for token:', token.substring(0, 10) + '...');

        const result = await magicLinkService.verifyMagicLink(token);

        console.log('[VerifyMagicLink] Verification successful, user:', result.user?.id);

        if (result.user) {
          setStatus('success');
          toast.success('Successfully signed in!');

          // Give the session more time to persist before redirecting
          console.log('[VerifyMagicLink] Waiting before redirect to:', redirectTo);
          setTimeout(() => {
            console.log('[VerifyMagicLink] Redirecting now with full page reload...');
            // Use window.location for full page reload to ensure session is picked up
            window.location.href = redirectTo;
          }, 2000);
        }
      } catch (error: any) {
        console.error('Verification error:', error);
        setStatus('error');

        if (error.message?.includes('already been used')) {
          setErrorMessage('This magic link has already been used.');
        } else if (error.message?.includes('expired')) {
          setErrorMessage('This magic link has expired. Please request a new one.');
        } else {
          setErrorMessage('Invalid or expired magic link.');
        }

        toast.error(errorMessage || 'Verification failed');
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          {status === 'loading' && (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <CardTitle className="text-2xl">Verifying your magic link</CardTitle>
              <CardDescription>Please wait a moment...</CardDescription>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Success!</CardTitle>
              <CardDescription>You're being signed in...</CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-2xl">Verification failed</CardTitle>
              <CardDescription className="text-red-600">{errorMessage}</CardDescription>
            </>
          )}
        </CardHeader>

        {status === 'error' && (
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
              <p className="mb-2">This magic link may have expired or already been used.</p>
              <p>Please request a new magic link to sign in.</p>
            </div>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-black hover:bg-gray-800 text-white"
            >
              Back to login
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default VerifyMagicLink;
